export interface DigitalProduct {
  id: string;
  productName: string;
  productType: 'Ready-Made Website' | 'Ready-Made Mobile App' | 'AI Assistant Template' | 'Automation Workflow' | 'Make.com Workflow' | 'n8n Workflow' | 'Code Pack' | 'Dashboard Template' | 'Social Media Pack' | 'Nexus Bundle';
  category: string;
  shortDescription: string;
  fullDescription: string;
  previewImage: string;
  previewVideoUrl?: string;
  demoUrl?: string;
  productScreenshots?: string[];
  includedFiles: string[];
  techStack: string[];
  integrations: string[];
  setupDifficulty: 'Beginner' | 'Intermediate' | 'Advanced' | 'Done-For-You Recommended';
  deliveryMethod: 'Digital Download' | 'Client Dashboard Delivery' | 'Nexus Hosting Setup' | 'GitHub / ZIP Export' | 'Workflow JSON Export' | 'Manual Setup Required' | 'Done-For-You Setup';
  licenceType: 'Personal Use' | 'Business Use' | 'Single Client Use' | 'Extended Licence' | 'Agency Licence' | 'Custom Licence';
  setupPrice: number; // London B2B pounds
  productPrice: number;
  monthlySupportPrice: number;
  paymentStatus: 'Free Preview' | 'Request Invoice' | 'Secure Checkout Available Soon' | 'Checkout Ready' | 'Subscription Required' | 'Quote Required';
  checkoutStatus: 'Not Connected' | 'Invoice Only' | 'Secure Checkout Ready' | 'Manual Payment Review' | 'Subscription Draft';
  productStatus: 'Draft' | 'Preview Ready' | 'Published' | 'Needs Review' | 'Coming Soon' | 'Disabled';
  buildStatus: 'Template Ready' | 'Setup Required' | 'Needs Testing' | 'Public Preview Ready' | 'Production Ready After Review';
  approvalStatus: 'Admin Draft' | 'Internal Review' | 'Approved for Preview' | 'Approved for Sale' | 'External Link Only' | 'Paused';
  tags: string[];
  featured: boolean;
  createdAt: string;
  updatedAt: string;
  
  // Newly introduced Nexus Asset Library metadata parameters
  assetName?: string;
  assetType?: 'Website Template' | 'Mobile App Template' | 'Dashboard Template' | 'UI Kit' | 'Figma File' | 'Adobe XD File' | 'Sketch File' | 'Code Pack' | 'Workflow Template' | 'Slide Deck' | 'Proposal Pack' | 'Brand Kit' | 'Social Pack' | 'Business Pack' | 'Nexus Bundle';
  fileFormats?: string[];
  platform?: string;
  standardPrice?: number;
  managedLaunchPrice?: number;
  discountEligible?: boolean;
  sourceType?: 'Original NexoraX Asset' | 'Licensed Third-Party Asset' | 'External Marketplace Link' | 'Affiliate Marketplace Link' | 'Client Custom Build' | 'Draft AI-Generated Asset' | 'Admin Research Item';
  externalSourceUrl?: string;
  licenceProofStatus?: 'Original Asset' | 'Licence Stored' | 'Licence Required' | 'External Link Only' | 'Not for Resale' | 'Needs Review';
  assetStatus?: 'Draft' | 'Preview Ready' | 'Published' | 'Needs Review' | 'Coming Soon' | 'Disabled';

  // Admin-only fields (hidden from general public)
  internalCost?: number;
  marginEstimate?: number;
  buildNotes?: string;
  riskNotes?: string;
  providerNotes?: string;
  supplierNotes?: string;
  fileStorageNotes?: string;
  privateLicenceNotes?: string;
}

export const INITIAL_DIGITAL_PRODUCTS: DigitalProduct[] = [
  {
    id: "ndp-1",
    productName: "AI Business Website Starter",
    productType: "Ready-Made Website",
    category: "SaaS Landing Page",
    shortDescription: "A premium AI-ready business website template with interactive features, CRM queue inputs and contact form.",
    fullDescription: "Deploy an elite digital presence. This ready-to-launch website starter includes: high-conversion interactive Hero components, a sleek interactive pricing calculator, service matrices with CSS neon-hover interactions, and deep compliance layouts ready for corporate customisation. Built with an optional client-side AI chat placeholder overlay, privacy notice centers, and responsive lead capture forms mapped directly into the Nexus billing system workflow.",
    previewImage: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800",
    demoUrl: "https://nexorax.co.uk/ai-startup-demo",
    productScreenshots: [
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=600",
      "https://images.unsplash.com/photo-1508921912186-1d1a45ebb3c1?auto=format&fit=crop&q=80&w=600"
    ],
    includedFiles: ["index.html", "src/App.tsx", "src/components/*.tsx", "vite.config.ts", "package.json", "tailwind.config.js"],
    techStack: ["React 19", "Vite", "Tailwind CSS", "Lucide Icons", "Motion (Framer)"],
    integrations: ["Nexus WorkSuite", "Nexus Mail Platform", "Stripe API Webhook"],
    setupDifficulty: "Beginner",
    deliveryMethod: "Nexus Hosting Setup",
    licenceType: "Business Use",
    setupPrice: 499,
    productPrice: 99,
    monthlySupportPrice: 29,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Invoice Only",
    productStatus: "Published",
    buildStatus: "Template Ready",
    approvalStatus: "Approved for Sale",
    tags: ["Business", "AI-ready", "Starter", "Modern UI"],
    featured: true,
    createdAt: "2026-06-01T08:00:00Z",
    updatedAt: "2026-06-18T10:00:00Z",
    internalCost: 45,
    marginEstimate: 54,
    buildNotes: "Initial baseline tested on Nginx and Node container environments.",
    riskNotes: "Ensure SSL certificates are generated correctly to prevent form submission fails.",
    providerNotes: "Uses standard Cloud Run or Docker templates.",
    supplierNotes: "None.",
    fileStorageNotes: "Asset available inside the Secure Google Drive library.",
    privateLicenceNotes: "Extended sub-license agreement valid for 1 year."
  },
  {
    id: "ndp-2",
    productName: "Restaurant & Takeaway Website Pack",
    productType: "Ready-Made Website",
    category: "Restaurant / Takeaway",
    shortDescription: "A sleek local food takeaway template with booking calendar, customizable visual menu and SEO tags.",
    fullDescription: "Take orders directly under your own digital flag. This high-density takeaway theme includes a sleek responsive menu with tabbed sorting (Starters, Main Courses, Desserts), automated booking request forms, localized SEO metatag configurations, maps integration module, and direct checkout call-to-actions to route callers back to the 08000 622827 hotlines.",
    previewImage: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&q=80&w=800",
    demoUrl: "https://nexorax.co.uk/menu-takeaway-demo",
    productScreenshots: [
      "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&q=80&w=600"
    ],
    includedFiles: ["index.html", "src/App.tsx", "src/menuData.ts", "package.json"],
    techStack: ["React", "Tailwind CSS", "Motion"],
    integrations: ["Google Maps Platform", "WhatsApp Business Ingress Link"],
    setupDifficulty: "Intermediate",
    deliveryMethod: "Client Dashboard Delivery",
    licenceType: "Single Client Use",
    setupPrice: 599,
    productPrice: 149,
    monthlySupportPrice: 39,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Invoice Only",
    productStatus: "Published",
    buildStatus: "Production Ready After Review",
    approvalStatus: "Approved for Sale",
    tags: ["Takeaway", "Restaurant", "Local SEO", "Menu"],
    featured: true,
    createdAt: "2026-06-02T12:00:00Z",
    updatedAt: "2026-06-18T10:15:00Z",
    internalCost: 65,
    marginEstimate: 84,
    buildNotes: "Menu can edit data visually. Integrates with standard Takeaway POS APIs."
  },
  {
    id: "ndp-3",
    productName: "AI Customer Support Assistant",
    productType: "AI Assistant Template",
    category: "Customer Support App",
    shortDescription: "Interactive intelligent chatbot for B2B FAQs, drafting support email replies and creating tickets inside WorkSuite.",
    fullDescription: "Automate responses to recurring enquiries without losing control. This template deploys an intelligent conversational interface. It reads from a structured JSON FAQ database and recommends answers instantly. IMPORTANT DISCLOSURE FOR GENERAL SAFETY: The system drafts proposed support replies automatically for user verification. A licensed human operator must review and authorize all communications manually before they are sent externally.",
    previewImage: "https://images.unsplash.com/photo-1531747118685-ca8fa6e08806?auto=format&fit=crop&q=80&w=800",
    productScreenshots: [
      "https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&q=80&w=600"
    ],
    includedFiles: ["server/supportAgent.ts", "src/components/SupportChat.tsx", "faq-data.json"],
    techStack: ["TypeScript", "@google/genai", "Express.js", "React Engine"],
    integrations: ["NexoraX Mail Engine", "Google Drive Archive System", "WorkSuite API"],
    setupDifficulty: "Done-For-You Recommended",
    deliveryMethod: "Done-For-You Setup",
    licenceType: "Business Use",
    setupPrice: 399,
    productPrice: 199,
    monthlySupportPrice: 49,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Manual Payment Review",
    productStatus: "Published",
    buildStatus: "Setup Required",
    approvalStatus: "Approved for Sale",
    tags: ["AI Chat", "Customer Support", "FAQ", "WorkSuite Linked"],
    featured: true,
    createdAt: "2026-06-03T09:00:00Z",
    updatedAt: "2026-06-18T10:20:00Z",
    internalCost: 110,
    marginEstimate: 289
  },
  {
    id: "ndp-4",
    productName: "Booking Assistant App",
    productType: "Ready-Made Mobile App",
    category: "Booking App",
    shortDescription: "A custom appointment slots client app with request queue, text reminders placeholder and service list.",
    fullDescription: "Perfect for barbers, local agencies, personal tutors, or accountants. Allows clients to pick services, request specific time slots, input notes, and view real-time booking statuses. Integrates cleanly with a secure admin dashboard where lead operators can click to approve, decline, or reschedule requests.",
    previewImage: "https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&q=80&w=800",
    productScreenshots: [
      "https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&q=80&w=600"
    ],
    includedFiles: ["src/components/Scheduler.tsx", "src/types/booking.ts", "server/routes/booking.ts"],
    techStack: ["React Native / React Web hybrid", "Tailwind CSS", "Node.js Schema"],
    integrations: ["Twilio SMS Gateway", "Google Calendar Sync Node"],
    setupDifficulty: "Intermediate",
    deliveryMethod: "Client Dashboard Delivery",
    licenceType: "Business Use",
    setupPrice: 599,
    productPrice: 249,
    monthlySupportPrice: 29,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Invoice Only",
    productStatus: "Published",
    buildStatus: "Template Ready",
    approvalStatus: "Approved for Sale",
    tags: ["Booking", "Mobile", "CRM Light", "Calendar"],
    featured: false,
    createdAt: "2026-06-04T15:00:00Z",
    updatedAt: "2026-06-18T10:25:00Z"
  },
  {
    id: "ndp-5",
    productName: "Health Tracker App Template",
    productType: "Ready-Made Mobile App",
    category: "Health App",
    shortDescription: "A premium mobile wellness tracking kit with customizable habit grids and status meters.",
    fullDescription: "A stylish template designed specifically for lifestyle coaches, personal trainers, and health advocates. Supports water tracking, customized workout schedule checklists, daily reflection text logging, and wellness tracker graphs. REQUIRED PUBLIC HEALTH NOTICE: This product is an tracking tool only, is NOT medical advice, is NOT software for medical purposes, and must never diagnose or treat any medical conditions.",
    previewImage: "https://images.unsplash.com/photo-1517838277536-f5f99be501cd?auto=format&fit=crop&q=80&w=800",
    productScreenshots: [
      "https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?auto=format&fit=crop&q=80&w=600"
    ],
    includedFiles: ["src/components/HealthTracker.tsx", "src/types/health.ts"],
    techStack: ["React 19 Hooks", "TailwindCSS Utility Classing", "SVG Line Charts"],
    integrations: ["Standard Device LocalStorage Link"],
    setupDifficulty: "Beginner",
    deliveryMethod: "Client Dashboard Delivery",
    licenceType: "Personal Use",
    setupPrice: 0,
    productPrice: 199,
    monthlySupportPrice: 0,
    paymentStatus: "Secure Checkout Available Soon",
    checkoutStatus: "Subscription Draft",
    productStatus: "Published",
    buildStatus: "Public Preview Ready",
    approvalStatus: "Approved for Preview",
    tags: ["Health", "Tracking", "Fitness", "Sleek Mobile"],
    featured: false,
    createdAt: "2026-06-05T14:00:00Z",
    updatedAt: "2026-06-18T10:30:00Z"
  },
  {
    id: "ndp-6",
    productName: "To-Do List Productivity App",
    productType: "Ready-Made Mobile App",
    category: "To-do List App",
    shortDescription: "An elegant productivity tracker centering checkbox tasks, custom tags and visual progress circles.",
    fullDescription: "Organise life or team sprints. Features customizable categories, task weights, due dates, reminder settings, search filters, priority highlighting (Urgent, High, Medium, Low), and high-contrast styling aligned directly with the NexoraX Galactic brand.",
    previewImage: "https://images.unsplash.com/photo-1484480974693-2ca0a714e6a5?auto=format&fit=crop&q=80&w=800",
    productScreenshots: [
      "https://images.unsplash.com/photo-1540350394557-8d14678e7f91?auto=format&fit=crop&q=80&w=600"
    ],
    includedFiles: ["src/components/TodoApp.tsx", "src/hooks/useLocalStorage.ts"],
    techStack: ["Vite Core", "Lucide Icons Pack", "Motion Micro-animations"],
    integrations: ["Device Local Storage API"],
    setupDifficulty: "Beginner",
    deliveryMethod: "Digital Download",
    licenceType: "Personal Use",
    setupPrice: 0,
    productPrice: 99,
    monthlySupportPrice: 0,
    paymentStatus: "Secure Checkout Available Soon",
    checkoutStatus: "Subscription Draft",
    productStatus: "Published",
    buildStatus: "Template Ready",
    approvalStatus: "Approved for Preview",
    tags: ["Productivity", "To-Do", "Simple", "Elegant"],
    featured: false,
    createdAt: "2026-06-06T10:00:00Z",
    updatedAt: "2026-06-18T10:35:00Z"
  },
  {
    id: "ndp-7",
    productName: "AI Research & Report Workflow",
    productType: "Automation Workflow",
    category: "AI Research Workflow",
    shortDescription: "An automated workflow template that parses search queries, drafts formatted summaries and alerts managers.",
    fullDescription: "A powerful server-side workflow blueprint that reads research queries, utilizes Nexus Search APIs for sector crawl grounding, feeds results to the Nexus Agent, generates multi-page HTML summaries, and logs tasks directly into the B2B administrative workspace. Note: Search API credits and premium token fees are billed separately.",
    previewImage: "https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?auto=format&fit=crop&q=80&w=800",
    productScreenshots: [
      "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=600"
    ],
    includedFiles: ["workflows/nexus-research-report.json", "scripts/runWorkflow.ts"],
    techStack: ["JSON Flow Scheme", "TypeScript Executor", "@google/genai"],
    integrations: ["Nexus Search APIs", "WorkSuite Sync Hook", "Google Drive Connector"],
    setupDifficulty: "Advanced",
    deliveryMethod: "Workflow JSON Export",
    licenceType: "Extended Licence",
    setupPrice: 0,
    productPrice: 149,
    monthlySupportPrice: 0,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Invoice Only",
    productStatus: "Published",
    buildStatus: "Public Preview Ready",
    approvalStatus: "Approved for Sale",
    tags: ["Research", "Workflows", "Automation", "AI Reports"],
    featured: true,
    createdAt: "2026-06-07T09:00:00Z",
    updatedAt: "2026-06-18T10:40:00Z"
  },
  {
    id: "ndp-8",
    productName: "Make.com Lead Capture Workflow",
    productType: "Make.com Workflow",
    category: "Lead Capture Workflow",
    shortDescription: "A ready-to-import blueprint for routing website leads to Google Sheets, Discord and Email lists.",
    fullDescription: "Import the JSON directly into your Make.com account. It triggers instantly on incoming Webhooks, checks input formats, pipes names and emails into active CRM folders, posts notifications to developer alerts channels, and drafts standard follow-up outreach lines automatically. Highly optimized B2B flow.",
    previewImage: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["make_blueprints/lead_capture_v3.json", "instructions.md"],
    techStack: ["Make.com Blueprint Schema", "JSON Webhook Structure"],
    integrations: ["Make.com Studio", "SMTP Mail API", "Discord / Slack API"],
    setupDifficulty: "Beginner",
    deliveryMethod: "Workflow JSON Export",
    licenceType: "Custom Licence",
    setupPrice: 299,
    productPrice: 99,
    monthlySupportPrice: 0,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Invoice Only",
    productStatus: "Published",
    buildStatus: "Template Ready",
    approvalStatus: "Approved for Sale",
    tags: ["Make.com", "Outreach", "CRM Integration", "Leads"],
    featured: false,
    createdAt: "2026-06-08T11:00:00Z",
    updatedAt: "2026-06-18T10:45:00Z"
  },
  {
    id: "ndp-9",
    productName: "n8n Customer Support Workflow",
    productType: "n8n Workflow",
    category: "Customer Support Workflow",
    shortDescription: "A self-hosted support routing n8n flow blueprint linking ticket forms to AI agents and notification grids.",
    fullDescription: "An advanced workflow blueprint optimized for n8n execution. Listens on database mutation ports, routes queries through a multi-branch logic (Billing Node vs Server Ingress Issues), generates pre-formulated reply templates with clear safety disclaimer placeholders, and updates active Trello or Jira status pipelines.",
    previewImage: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["n8n_flows/support_router.json", "setup_guide_n8n.html"],
    techStack: ["n8n Flow Scheme", "JSON Modules Schema"],
    integrations: ["n8n Platform", "Gemini Node Configuration", "Jira API Client"],
    setupDifficulty: "Intermediate",
    deliveryMethod: "Workflow JSON Export",
    licenceType: "Agency Licence",
    setupPrice: 299,
    productPrice: 99,
    monthlySupportPrice: 0,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Invoice Only",
    productStatus: "Published",
    buildStatus: "Template Ready",
    approvalStatus: "Approved for Sale",
    tags: ["n8n", "Support", "Workflow Automation", "Self-Hosted"],
    featured: false,
    createdAt: "2026-06-09T16:00:00Z",
    updatedAt: "2026-06-18T10:50:00Z"
  },
  {
    id: "ndp-10",
    productName: "Python Automation Script Pack",
    productType: "Code Pack",
    category: "Python Script",
    shortDescription: "A robust compilation of server helper tooling for CSV crunching, folder sorting and image optimization.",
    fullDescription: "A clean collection of 10 ready-to-run Python utilities. Includes recursive file organisers, image converters, CSV processing engines for tax estimates, robust error-logging templates, and simple API requests scripts with proper security parameters. A perfect workspace utility pack.",
    previewImage: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["scripts/cleanup.py", "scripts/csv_formatter.py", "scripts/compress_images.py", "requirements.txt"],
    techStack: ["Python 3.11", "pandas", "Pillow", "requests"],
    integrations: ["Command Line Interface", "Local File Systems"],
    setupDifficulty: "Intermediate",
    deliveryMethod: "GitHub / ZIP Export",
    licenceType: "Custom Licence",
    setupPrice: 0,
    productPrice: 79,
    monthlySupportPrice: 0,
    paymentStatus: "Secure Checkout Available Soon",
    checkoutStatus: "Subscription Draft",
    productStatus: "Published",
    buildStatus: "Template Ready",
    approvalStatus: "Approved for Sale",
    tags: ["Python", "Scripting", "CSV Tooling", "Developer Core"],
    featured: false,
    createdAt: "2026-06-10T10:00:00Z",
    updatedAt: "2026-06-18T10:55:00Z"
  },
  {
    id: "ndp-11",
    productName: "React Admin Dashboard Starter",
    productType: "Dashboard Template",
    category: "Business Dashboard",
    shortDescription: "An elite frontend template featuring modern metric cards, D3 visual grids and a responsive layout.",
    fullDescription: "The ultimate dashboard foundation for enterprise products. Styled in a futuristic galactic dark visual theme with neon cyan borders. Built perfectly with fully functional sorting matrices, sidebar and tab selectors, modular layouts, and beautifully configured analytics components.",
    previewImage: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800",
    demoUrl: "https://nexorax.co.uk/admin-dashboard-demo",
    includedFiles: ["src/components/AdminCorePanel.tsx", "src/hooks/useMetrics.ts", "src/styles/terminal.css"],
    techStack: ["React 19", "Tailwind CSS Utility Engine", "Recharts & D3.js"],
    integrations: ["Secure API Gateway proxies", "JWT Authentication flow placeholders"],
    setupDifficulty: "Advanced",
    deliveryMethod: "GitHub / ZIP Export",
    licenceType: "Agency Licence",
    setupPrice: 0,
    productPrice: 149,
    monthlySupportPrice: 0,
    paymentStatus: "Secure Checkout Available Soon",
    checkoutStatus: "Subscription Draft",
    productStatus: "Published",
    buildStatus: "Production Ready After Review",
    approvalStatus: "Approved for Sale",
    tags: ["React Panel", "Admin UI", "D3 Charts", "Dashboard"],
    featured: true,
    createdAt: "2026-06-11T13:00:00Z",
    updatedAt: "2026-06-18T11:00:00Z"
  },
  {
    id: "ndp-12",
    productName: "AI Social Media Automation Pack",
    productType: "Social Media Pack",
    category: "Social Media Workflow",
    shortDescription: "Complete campaign setup for caption generation, auto-render queuing and posting schedules.",
    fullDescription: "Deploy active campaign loops. Integrates caption draft automation, custom visual design coordinates, structured scheduler spreadsheets, and workflow parameters. IMPORTANT REVIEW DIRECTIVE: Automation drafts visual post parameters and outline caption ideas. The user must manually review and hit Approve before postings occur.",
    previewImage: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["workflows/socialAuto.json", "scripts/generatePostCaptions.ts"],
    techStack: ["TypeScript Microservice", "@google/genai", "JSON Template Structure"],
    integrations: ["Buffer API Link", "LinkedIn / Twitter Developer portals", "Secure Key API Manager"],
    setupDifficulty: "Done-For-You Recommended",
    deliveryMethod: "Done-For-You Setup",
    licenceType: "Extended Licence",
    setupPrice: 499,
    productPrice: 199,
    monthlySupportPrice: 49,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Manual Payment Review",
    productStatus: "Published",
    buildStatus: "Setup Required",
    approvalStatus: "Approved for Sale",
    tags: ["Social Media", "Marketing", "Outreach Scheduler", "Content Automation"],
    featured: true,
    createdAt: "2026-06-12T09:00:00Z",
    updatedAt: "2026-06-18T11:10:00Z"
  },
  {
    id: "ndp-13",
    productName: "Vanguard Enterprise UI Kit",
    productType: "Social Media Pack", // Maps back to supported productType
    assetName: "Vanguard Enterprise UI Kit",
    assetType: "UI Kit",
    category: "Figma UI Kits",
    shortDescription: "A massive, ultra-polished modern Figma design system and UI kit tailored for corporate SaaS, FinTech dashboards, and luxury design workflows.",
    fullDescription: "Standardize your design workflow immediately. This premium design asset includes 500+ responsive components, nested auto-layouts, custom vector iconography, global colour tokens matching the NexoraX neon aesthetic, light/dark responsive dashboards, high-fidelity wireframe elements, and ready-made landing page designs.",
    previewImage: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["vanguard_enterprise_system_v1.2.fig", "style_guide.pdf"],
    fileFormats: [".fig", ".pdf"],
    techStack: ["Figma Design Token Config", "Nested Auto-Layout 5.0", "Responsive Components"],
    integrations: ["Figma Variables API", "Storybook CSS Mappings"],
    platform: "Figma",
    setupDifficulty: "Beginner",
    deliveryMethod: "Client Dashboard Delivery",
    licenceType: "Agency Licence",
    setupPrice: 0,
    productPrice: 129,
    monthlySupportPrice: 0,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Invoice Only",
    productStatus: "Published",
    buildStatus: "Template Ready",
    approvalStatus: "Approved for Sale",
    sourceType: "Original NexoraX Asset",
    licenceProofStatus: "Original Asset",
    discountEligible: true,
    tags: ["UI Kit", "Figma", "Design System", "Modernist UI"],
    featured: true,
    createdAt: "2026-06-13T10:00:00Z",
    updatedAt: "2026-06-18T11:20:00Z"
  },
  {
    id: "ndp-14",
    productName: "NexoraX Premium B2B Pitch Deck",
    productType: "Social Media Pack",
    assetName: "NexoraX Premium B2B Pitch Deck",
    assetType: "Slide Deck",
    category: "Pitch Decks",
    shortDescription: "A powerful, slide-by-slide investor and client presentation template optimized for B2B tech proposals, grant funding, and seed rounds.",
    fullDescription: "Close contracts with elegance. This slide deck template includes: Problem & Solution slides with neon custom infographics, interactive market size D3-style layouts, revenue ticker mock layouts, team profile cards, financial forecast tables, and a strong call-to-action slide mapping back to the zero-fee Pakistan Node Sync and direct-line hotline protocols.",
    previewImage: "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["nexorax_b2b_pitch_deck_v2.0.pptx", "presentation_guide.pdf", "graphics_pack.key"],
    fileFormats: [".pptx", ".key", ".pdf"],
    techStack: ["PowerPoint SmartArt", "Keynote Styles", "Adobe Illustrator Vector exports"],
    integrations: ["Google Slides Sync Template"],
    platform: "PowerPoint / Keynote",
    setupDifficulty: "Beginner",
    deliveryMethod: "Digital Download",
    licenceType: "Personal Use",
    setupPrice: 0,
    productPrice: 69,
    monthlySupportPrice: 0,
    paymentStatus: "Secure Checkout Available Soon",
    checkoutStatus: "Subscription Draft",
    productStatus: "Published",
    buildStatus: "Template Ready",
    approvalStatus: "Approved for Sale",
    sourceType: "Original NexoraX Asset",
    licenceProofStatus: "Original Asset",
    discountEligible: false,
    tags: ["Pitch Deck", "Business Template", "PowerPoint", "Keynote", "Proposal"],
    featured: true,
    createdAt: "2026-06-14T09:00:00Z",
    updatedAt: "2026-06-18T11:30:00Z"
  },
  {
    id: "ndp-15",
    productName: "Stripe Subscription Webhook Pack",
    productType: "Code Pack",
    assetName: "Stripe Subscription Webhook Pack",
    assetType: "Code Pack",
    category: "Auth & Payments Packs",
    shortDescription: "A robust Express.js & TypeScript microservice template configured for handling complex multi-tier Stripe subscription webhook events safely.",
    fullDescription: "Secure your financial backend endpoints against missed payment states. This microservice features: raw event buffer parsers, cryptographically secure Stripe signature validations, instant Firestore db updater loops, customized receipt template mail triggers, automated subscription grace period alerts, and detailed diagnostic error logging output to local secure partitions.",
    previewImage: "https://images.unsplash.com/photo-1563013544-824ae1d704d3?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["package.json", "src/stripeWebhook.ts", "src/db/updater.ts", ".env.example"],
    fileFormats: [".ts", ".json"],
    techStack: ["TypeScript", "Express.js", "Stripe SDK v14", "Drizzle ORM"],
    integrations: ["Stripe Developer Dashboard", "Firestore Database", "Postmark/Sendgrid SMTP"],
    platform: "Node.js Core",
    setupDifficulty: "Advanced",
    deliveryMethod: "GitHub / ZIP Export",
    licenceType: "Business Use",
    setupPrice: 200,
    productPrice: 119,
    monthlySupportPrice: 19,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Invoice Only",
    productStatus: "Published",
    buildStatus: "Production Ready After Review",
    approvalStatus: "Approved for Sale",
    sourceType: "Original NexoraX Asset",
    licenceProofStatus: "Original Asset",
    discountEligible: true,
    tags: ["Stripe", "Payment Gateway", "Webhook", "Security Audit Ready", "Microservice"],
    featured: false,
    createdAt: "2026-06-15T15:00:00Z",
    updatedAt: "2026-06-18T11:40:05Z"
  },
  {
    id: "ndp-16",
    productName: "Affiliate Bootstrap Corporate Baseline",
    productType: "Ready-Made Website",
    assetName: "Affiliate Bootstrap Corporate Baseline",
    assetType: "Website Template",
    category: "Affiliate Marketplace References",
    shortDescription: "Third-party high-performance responsive corporate template indexed for external procurement through verified affiliate link routing.",
    fullDescription: "A luxury B2B corporate template with high SEO performance. IMPORTANT DIRECTIVE: This item is listed as an external marketplace reference. Purchase, file licensing, and code support terms are fully governed by the external publisher. Local file delivery is disabled.",
    previewImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["External Resource - File Delivery via Marketplace Provider Only"],
    fileFormats: [".html", ".css", ".js"],
    techStack: ["Bootstrap 5", "HTML5 Canvas", "jQuery Premium Animations"],
    integrations: ["External Supplier Account"],
    platform: "Bootstrap Core",
    setupDifficulty: "Intermediate",
    deliveryMethod: "Manual Setup Required",
    licenceType: "Personal Use",
    setupPrice: 399,
    productPrice: 49,
    monthlySupportPrice: 0,
    paymentStatus: "Quote Required",
    checkoutStatus: "Not Connected",
    productStatus: "Published",
    buildStatus: "Needs Testing",
    approvalStatus: "External Link Only",
    sourceType: "Affiliate Marketplace Link",
    externalSourceUrl: "https://themeforest.net/category/corporate",
    licenceProofStatus: "External Link Only",
    discountEligible: false,
    tags: ["External Link", "Bootstrap", "Corporate UI", "Marketplace Reference"],
    featured: false,
    createdAt: "2026-06-16T12:00:00Z",
    updatedAt: "2026-06-18T11:45:00Z"
  },
  {
    id: "ndp-17",
    productName: "Corporate Grant Proposal Template Pack",
    productType: "Social Media Pack",
    assetName: "Corporate Grant Proposal Template Pack",
    assetType: "Business Pack",
    category: "Social Media Hub Items",
    shortDescription: "A high-fidelity structured suite of Word, Excel, and PDF proposal plans designed for UK & Global £150,000 grant application processes.",
    fullDescription: "Everything you need to successfully format and compile enterprise B2B proposals and community-governed grants. Includes structured cashflow schedules, social impact statements, standard corporate bio sheets, and AWS/Cloud infrastructure setup proof templates.",
    previewImage: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["grant_proposal_master.docx", "cashflow_projections.xlsx", "appendix_infrastructure.docx"],
    fileFormats: [".docx", ".xlsx", ".pdf"],
    techStack: ["Microsoft Word Formatting", "Excel Accounting Formula Maps"],
    integrations: ["Local File Explorer"],
    platform: "Word / Excel Pack",
    setupDifficulty: "Beginner",
    deliveryMethod: "Digital Download",
    licenceType: "Extended Licence",
    setupPrice: 0,
    productPrice: 199,
    monthlySupportPrice: 0,
    paymentStatus: "Secure Checkout Available Soon",
    checkoutStatus: "Subscription Draft",
    productStatus: "Published",
    buildStatus: "Template Ready",
    approvalStatus: "Approved for Sale",
    sourceType: "Original NexoraX Asset",
    licenceProofStatus: "Original Asset",
    discountEligible: true,
    tags: ["Grants", "Grant Funding Pack", "Business Proposal", "Excel Financials"],
    featured: true,
    createdAt: "2026-06-17T09:00:00Z",
    updatedAt: "2026-06-18T11:55:00Z"
  },
  {
    id: "ndp-18",
    productName: "AI Voice Assistant Template",
    productType: "AI Assistant Template",
    category: "AI Tools / Add-ons",
    shortDescription: "A branded AI voice assistant for websites and mobile apps that welcomes visitors, explains services, guides users, and captures leads.",
    fullDescription: "Deploy a high-end AI voice representative right on your domain. This template includes pre-recorded baseline vocal nodes, interactive speech-to-text input filters, visual soundwave canvas animations, and automated lead capture hooks.",
    previewImage: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["src/components/VoiceAssistant.tsx", "src/hooks/useVoiceSynthesis.ts", "voice-config.json"],
    techStack: ["React 19", "Web Speech API", "Motion", "Tailwind CSS"],
    integrations: ["NexoraX Lead Ingress Engine", "WorkSuite CRM API"],
    setupDifficulty: "Beginner",
    deliveryMethod: "Client Dashboard Delivery",
    licenceType: "Business Use",
    setupPrice: 199,
    productPrice: 99,
    monthlySupportPrice: 29,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Subscription Draft",
    productStatus: "Published",
    buildStatus: "Template Ready",
    approvalStatus: "Approved for Sale",
    tags: ["AI Voice", "Voice Assistant", "Lead Capture"],
    featured: true,
    createdAt: "2026-06-28T22:00:00Z",
    updatedAt: "2026-06-28T22:00:00Z"
  },
  {
    id: "ndp-19",
    productName: "AI Customer Support Assistant Template",
    productType: "AI Assistant Template",
    category: "AI Tools / Add-ons",
    shortDescription: "An automated support chatbot template designed to answer B2B FAQs, guide customers, and assist during onboarding.",
    fullDescription: "Streamline customer queries. This interactive chatbot hooks directly into structured FAQ document lists, parses user questions, guides them through package selections, and triggers booking or invoice draft requests.",
    previewImage: "https://images.unsplash.com/photo-1531747118685-ca8fa6e08806?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["src/components/SupportAssistant.tsx", "src/data/supportFaq.json"],
    techStack: ["TypeScript", "React", "Lucide Icons", "Express.js"],
    integrations: ["NexoraX Mail Engine", "WorkSuite API"],
    setupDifficulty: "Beginner",
    deliveryMethod: "Client Dashboard Delivery",
    licenceType: "Business Use",
    setupPrice: 199,
    productPrice: 99,
    monthlySupportPrice: 29,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Subscription Draft",
    productStatus: "Published",
    buildStatus: "Template Ready",
    approvalStatus: "Approved for Sale",
    tags: ["AI Chat", "Customer Support", "FAQ"],
    featured: true,
    createdAt: "2026-06-28T22:00:00Z",
    updatedAt: "2026-06-28T22:00:00Z"
  },
  {
    id: "ndp-20",
    productName: "Live Driver Tracking UI Kit",
    productType: "Code Pack",
    category: "Logistics / Tracking",
    shortDescription: "A premium live map tracking interface showing driver locations, progress routes, status updates, and ETA widgets.",
    fullDescription: "Provide real-time visual progress. This visual package contains customizable map marker states, driver card displays, and estimation widgets. Perfect for couriers, taxis, logistics, or field services.",
    previewImage: "https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["src/components/DriverMap.tsx", "src/hooks/useDriverGPS.ts", "map-styles.json"],
    techStack: ["React", "Leaflet Maps API", "CSS Grid", "Lucide Icons"],
    integrations: ["Mapbox SDK / Google Maps API", "GPS Location Sensor"],
    setupDifficulty: "Intermediate",
    deliveryMethod: "Client Dashboard Delivery",
    licenceType: "Extended Licence",
    setupPrice: 299,
    productPrice: 199,
    monthlySupportPrice: 39,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Subscription Draft",
    productStatus: "Published",
    buildStatus: "Template Ready",
    approvalStatus: "Approved for Sale",
    tags: ["Tracking", "Maps", "Logistics", "Live Location"],
    featured: true,
    createdAt: "2026-06-28T22:00:00Z",
    updatedAt: "2026-06-28T22:00:00Z"
  },
  {
    id: "ndp-21",
    productName: "Driver Mobile App UI Kit",
    productType: "Code Pack",
    category: "Logistics / Tracking",
    shortDescription: "A mobile driver interface kit for accepting jobs, job statuses, sharing GPS location, and dispatch updates.",
    fullDescription: "Streamline mobile operations. This mobile-first kit contains interface grids for active route details, job accepting notifications, current progress state controls, and photo upload proof sections.",
    previewImage: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["src/mobile/DriverApp.tsx", "src/mobile/JobList.tsx", "gps-scheduler.ts"],
    techStack: ["React Native / React hybrid", "Tailwind CSS", "GPS Location Node"],
    integrations: ["Geolocation API", "Firebase Push Notification Gateway"],
    setupDifficulty: "Advanced",
    deliveryMethod: "Client Dashboard Delivery",
    licenceType: "Extended Licence",
    setupPrice: 399,
    productPrice: 249,
    monthlySupportPrice: 49,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Subscription Draft",
    productStatus: "Published",
    buildStatus: "Template Ready",
    approvalStatus: "Approved for Sale",
    tags: ["Driver App", "Mobile", "Logistics", "Tracking"],
    featured: true,
    createdAt: "2026-06-28T22:00:00Z",
    updatedAt: "2026-06-28T22:00:00Z"
  },
  {
    id: "ndp-22",
    productName: "Customer Tracking Page Template",
    productType: "Code Pack",
    category: "Logistics / Tracking",
    shortDescription: "Secure customer-facing progress link showing driver progress, ETA, order details, and contact options.",
    fullDescription: "Build customer confidence. This standalone customer progress layout includes ETA countdowns, live location marker progress lines, order summary boxes, and direct hotline callback controls.",
    previewImage: "https://images.unsplash.com/photo-1557200134-90327ee9fafa?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["src/pages/CustomerTracking.tsx", "src/components/ETAWidget.tsx"],
    techStack: ["React", "CSS Grid", "Lucide Icons", "Motion"],
    integrations: ["Hetzner Gateway", "Secure Token Dispatch API"],
    setupDifficulty: "Beginner",
    deliveryMethod: "Client Dashboard Delivery",
    licenceType: "Single Client Use",
    setupPrice: 199,
    productPrice: 99,
    monthlySupportPrice: 19,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Subscription Draft",
    productStatus: "Published",
    buildStatus: "Template Ready",
    approvalStatus: "Approved for Sale",
    tags: ["Customer Link", "Tracking", "Logistics", "ETA"],
    featured: true,
    createdAt: "2026-06-28T22:00:00Z",
    updatedAt: "2026-06-28T22:00:00Z"
  },
  {
    id: "ndp-23",
    productName: "Admin Driver Dashboard Template",
    productType: "Dashboard Template",
    category: "Logistics / Tracking",
    shortDescription: "A central control dashboard template for tracking drivers, live jobs, status logs, and dispatch queues.",
    fullDescription: "Complete command of your mobile fleet. Features multi-driver status maps, dispatch order logs, route optimization graphs, and customer feedback lists.",
    previewImage: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["src/dashboard/DriverMonitor.tsx", "src/dashboard/DispatchQueue.tsx"],
    techStack: ["React", "Recharts D3 Engine", "Leaflet Maps API", "Tailwind CSS"],
    integrations: ["NexoraX Dispatch API", "WorkSuite Task Queue"],
    setupDifficulty: "Intermediate",
    deliveryMethod: "Client Dashboard Delivery",
    licenceType: "Agency Licence",
    setupPrice: 499,
    productPrice: 299,
    monthlySupportPrice: 59,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Subscription Draft",
    productStatus: "Published",
    buildStatus: "Template Ready",
    approvalStatus: "Approved for Sale",
    tags: ["Dashboard", "Fleet Management", "Logistics", "Tracking"],
    featured: true,
    createdAt: "2026-06-28T22:00:00Z",
    updatedAt: "2026-06-28T22:00:00Z"
  },
  {
    id: "ndp-24",
    productName: "Fleet Operations Dashboard Pack",
    productType: "Dashboard Template",
    category: "Logistics / Tracking",
    shortDescription: "A comprehensive fleet dashboard package for analyzing logistics, delivery performance, and driver metrics.",
    fullDescription: "Fine-tune logistics efficiency. Includes fuel tracking spreadsheets, driver performance tables, customer review analytics, and vehicle status notifications.",
    previewImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["src/dashboard/FleetAnalytics.tsx", "src/dashboard/VehicleList.tsx"],
    techStack: ["React", "Chart.js", "Tailwind CSS", "Lucide Icons"],
    integrations: ["NexoraX Analytics API", "Google Sheets Live Sync Node"],
    setupDifficulty: "Intermediate",
    deliveryMethod: "Client Dashboard Delivery",
    licenceType: "Agency Licence",
    setupPrice: 499,
    productPrice: 299,
    monthlySupportPrice: 59,
    paymentStatus: "Request Invoice",
    checkoutStatus: "Subscription Draft",
    productStatus: "Published",
    buildStatus: "Template Ready",
    approvalStatus: "Approved for Sale",
    tags: ["Dashboard", "Fleet Management", "Logistics", "Analytics"],
    featured: true,
    createdAt: "2026-06-28T22:00:00Z",
    updatedAt: "2026-06-28T22:00:00Z"
  },
  {
    id: "ndp-25",
    productName: "Full Live Driver Tracking System",
    productType: "Nexus Bundle",
    category: "Logistics / Tracking",
    shortDescription: "A complete custom ecosystem connecting driver app, customer live page, and fleet admin control dashboard.",
    fullDescription: "The ultimate tracking infrastructure for delivery, taxi, logistics, or workforce operations. Connects mobile GPS tracking endpoints, secure backend servers, estimate estimation APIs, estimated arrival, and centralized administrator dashboard screens.",
    previewImage: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&q=80&w=800",
    includedFiles: ["server/dispatcher.ts", "server/locationHub.ts", "src/App.tsx"],
    techStack: ["Node.js Server", "WebSockets Engine", "React Engine", "Leaflet Maps API"],
    integrations: ["Hetzner Server Cloud", "NexoraX Core Database API", "Firebase Push Notification Gateway"],
    setupDifficulty: "Done-For-You Recommended",
    deliveryMethod: "Done-For-You Setup",
    licenceType: "Extended Licence",
    setupPrice: 0,
    productPrice: 0,
    monthlySupportPrice: 0,
    paymentStatus: "Quote Required",
    checkoutStatus: "Not Connected",
    productStatus: "Published",
    buildStatus: "Setup Required",
    approvalStatus: "Approved for Sale",
    tags: ["Tracking", "Maps", "Logistics", "Full System"],
    featured: true,
    createdAt: "2026-06-28T22:00:00Z",
    updatedAt: "2026-06-28T22:00:00Z"
  }
];

